/**
 * FaqAnswer
 *
 * Renders a plain-text FAQ answer (from the CMS or the i18n fallback) and
 * turns any URL inside it into a clickable link. Same-origin URLs navigate
 * in-app via react-router; external URLs open in a new tab.
 */

import React from "react";
import { Link } from "react-router-dom";

const URL_PATTERN = /(https?:\/\/[^\s]+)/g;
const TRAILING_PUNCTUATION = /[.,:;)]+$/;

const LINK_CLASSES =
  "break-all font-semibold text-[#bf0035] underline underline-offset-2 transition hover:text-[#a6002e]";

type Segment =
  | { type: "text"; value: string }
  | { type: "link"; href: string; trailing: string };

const splitIntoSegments = (text: string): Segment[] =>
  text.split(URL_PATTERN).map((part): Segment => {
    if (!/^https?:\/\//.test(part)) return { type: "text", value: part };
    const trailing = part.match(TRAILING_PUNCTUATION)?.[0] ?? "";
    return {
      type: "link",
      href: part.slice(0, part.length - trailing.length),
      trailing,
    };
  });

const isSameOrigin = (href: string): URL | null => {
  try {
    const url = new URL(href);
    return url.origin === window.location.origin ? url : null;
  } catch {
    return null;
  }
};

const FaqAnswer: React.FC<{ answer: string }> = ({ answer }) => (
  <p className="max-w-2xl pb-6 leading-7 text-gray-600">
    {splitIntoSegments(answer).map((segment, index) => {
      if (segment.type === "text") {
        return <React.Fragment key={index}>{segment.value}</React.Fragment>;
      }
      const internal = isSameOrigin(segment.href);
      return (
        <React.Fragment key={index}>
          {internal ? (
            <Link
              to={`${internal.pathname}${internal.search}${internal.hash}`}
              dir="ltr"
              className={LINK_CLASSES}
            >
              {segment.href}
            </Link>
          ) : (
            <a
              href={segment.href}
              target="_blank"
              rel="noopener noreferrer"
              dir="ltr"
              className={LINK_CLASSES}
            >
              {segment.href}
            </a>
          )}
          {segment.trailing}
        </React.Fragment>
      );
    })}
  </p>
);

export default FaqAnswer;
